'use client';

import { c as _c } from "react/compiler-runtime";
import NextImage from 'next/image';
import type { ReactNode } from 'react';
import { createContext, use } from 'react';
import { FavoriteButton } from '@src/components/FavoriteButton/FavoriteButton';
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink';
import { ShareButton } from '@src/components/ShareButton/ShareButton';
import { cn } from '@src/utils/tailwind';
export type BaseTileColumns = 2 | 4 | 5;
export const baseTileRowImageSizeClass = 'h-27.25 w-40 min-w-40 shrink-0';
export const baseTileDesktopSizeClass: Record<BaseTileColumns, string> = {
  2: 'lg:w-[249.6px] lg:h-[171px] lg:min-w-[249.6px]',
  4: 'lg:w-[322px] lg:h-[220px] lg:min-w-[322px]',
  5: 'lg:w-[249.6px] lg:h-[171px] lg:min-w-[249.6px]'
};
export const baseTileSlideWidthClass: Record<BaseTileColumns, string> = {
  2: 'lg:min-w-[249.6px] lg:max-w-[249.6px]',
  4: 'lg:min-w-[322px] lg:max-w-[322px]',
  5: 'lg:min-w-[249.6px] lg:max-w-[249.6px]'
};
type BaseTileProps = {
  entityId?: string;
  entityType?: CocoApi.EntityType;
  title: string;
  violator?: React.ReactNode | string;
  imageUrl: string;
  href: string;
  description?: ReactNode;
  descriptionLong?: string;
  layout?: 'column' | 'row';
  rounded?: 'large' | 'small';
  footerText?: string;
  isFavoritable?: boolean;
  isFavorited?: boolean;
  shareUrl?: string;
  showFavorite?: boolean;
  showShare?: boolean;
  onFavoriteClick?: (isFavorited: boolean) => void;
  onShareClick?: () => void;
  className?: string;
  progress?: number;
  isLocked?: boolean;
  isPlayable?: boolean;
  columns?: 2 | 4 | 5;
  fluid?: boolean;
  openInNewTab?: boolean;
  onClick?: () => void;
};
type Props = {
  className?: string;
  onClick?: () => void;
};
const BaseTileContext = createContext<BaseTileProps>({
  title: '',
  imageUrl: '',
  href: '',
  layout: 'column',
  rounded: 'large',
  descriptionLong: '',
  footerText: '',
  isFavoritable: false,
  isFavorited: false,
  shareUrl: undefined,
  showFavorite: false,
  showShare: false,
  onFavoriteClick: undefined,
  onShareClick: undefined,
  className: '',
  progress: undefined,
  isLocked: false,
  isPlayable: false,
  columns: 4,
  fluid: false,
  openInNewTab: false
});
export const useBaseTile = () => {
  return use(BaseTileContext);
};
const Provider = t0 => {
  const $ = _c(6);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== props) {
    t1 = <BaseTileContext.Provider value={props}>{children}</BaseTileContext.Provider>;
    $[3] = children;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const Wrapper = t0 => {
  const $ = _c(8);
  const {
    children,
    className
  } = t0;
  const {
    className: wrapperClassName,
    columns: t1,
    fluid,
    layout
  } = useBaseTile();
  const columns = t1 === undefined ? 4 : t1;
  const isFluidColumn = fluid && layout === "column";
  let t2;
  if ($[0] !== className || $[1] !== columns || $[2] !== isFluidColumn || $[3] !== wrapperClassName) {
    t2 = cn("group flex flex-col gap-2", isFluidColumn ? "w-full" : ["min-w-40", "max-w-40", baseTileSlideWidthClass[columns]], className, wrapperClassName);
    $[0] = className;
    $[1] = columns;
    $[2] = isFluidColumn;
    $[3] = wrapperClassName;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children || $[6] !== t2) {
    t3 = <div className={t2}>{children}</div>;
    $[5] = children;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const Link = t0 => {
  const $ = _c(16);
  const {
    children,
    className
  } = t0;
  const {
    href,
    className: wrapperClassName,
    columns: t1,
    layout,
    openInNewTab,
    onClick,
    fluid
  } = useBaseTile();
  const columns = t1 === undefined ? 4 : t1;
  let t2;
  if ($[0] !== href) {
    t2 = href.startsWith("/") || href.startsWith("http") ? href : `/${href}`;
    $[0] = href;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const resolvedHref = t2;
  const isRow = layout === "row";
  const isFluidColumn = fluid && !isRow;
  const t3 = openInNewTab ? "_blank" : undefined;
  const t4 = openInNewTab ? "noopener noreferrer" : undefined;
  const t5 = isRow ? "w-full flex-row items-start gap-4" : "flex-col";
  let t6;
  if ($[2] !== className || $[3] !== columns || $[4] !== isFluidColumn || $[5] !== isRow || $[6] !== t5 || $[7] !== wrapperClassName) {
    t6 = cn("group flex gap-2", t5, isFluidColumn ? "w-full" : !isRow && ["min-w-40", "max-w-40", baseTileSlideWidthClass[columns]], className, wrapperClassName);
    $[2] = className;
    $[3] = columns;
    $[4] = isFluidColumn;
    $[5] = isRow;
    $[6] = t5;
    $[7] = wrapperClassName;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== children || $[10] !== onClick || $[11] !== resolvedHref || $[12] !== t3 || $[13] !== t4 || $[14] !== t6) {
    t7 = <LocalizedLink href={resolvedHref} onClick={onClick} target={t3} rel={t4} className={t6}>{children}</LocalizedLink>;
    $[9] = children;
    $[10] = onClick;
    $[11] = resolvedHref;
    $[12] = t3;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
const Thumbnail = t0 => {
  const $ = _c(9);
  const {
    children,
    className,
    onClick
  } = t0;
  const {
    columns: t1,
    fluid
  } = useBaseTile();
  const columns = t1 === undefined ? 4 : t1;
  const Tag = onClick ? "button" : "div";
  let t2;
  if ($[0] !== className || $[1] !== columns || $[2] !== fluid) {
    t2 = cn("relative", fluid ? "aspect-322/220 w-full" : [baseTileRowImageSizeClass, baseTileDesktopSizeClass[columns]], className);
    $[0] = className;
    $[1] = columns;
    $[2] = fluid;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== Tag || $[5] !== children || $[6] !== onClick || $[7] !== t2) {
    t3 = <Tag className={t2} onClick={onClick}>{children}</Tag>;
    $[4] = Tag;
    $[5] = children;
    $[6] = onClick;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const Image = t0 => {
  const $ = _c(9);
  const {
    className
  } = t0;
  const {
    imageUrl,
    rounded,
    title,
    columns: t1,
    fluid
  } = useBaseTile();
  const columns = t1 === undefined ? 4 : t1;
  let t2;
  if ($[0] !== className || $[1] !== columns || $[2] !== fluid || $[3] !== rounded) {
    t2 = cn("border-transparent object-cover opacity-100 outline-[3px] outline-offset-[-3px] outline-transparent transition-colors duration-150 ease-in-out group-hover:outline-white", fluid ? "aspect-322/220 w-full" : [baseTileRowImageSizeClass, baseTileDesktopSizeClass[columns]], {
      "rounded-[30px]": rounded === "large",
      "rounded-[14px]": rounded === "small"
    }, className);
    $[0] = className;
    $[1] = columns;
    $[2] = fluid;
    $[3] = rounded;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== imageUrl || $[6] !== t2 || $[7] !== title) {
    t3 = <NextImage className={t2} src={imageUrl} alt={title} width={900} height={700} loading="lazy" />;
    $[5] = imageUrl;
    $[6] = t2;
    $[7] = title;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const Content = t0 => {
  const $ = _c(6);
  const {
    children,
    className,
    onClick
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex flex-col", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== onClick || $[4] !== t1) {
    t2 = <div className={t1} onClick={onClick} role="button" tabIndex={0}>{children}</div>;
    $[2] = children;
    $[3] = onClick;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const Violator = () => {
  const $ = _c(2);
  const {
    violator
  } = useBaseTile();
  if (!violator) {
    return null;
  }
  if (typeof violator === "string") {
    let t0;
    if ($[0] !== violator) {
      t0 = <p className="text-body-md font-semibold text-bodi-accent-bright-500">{violator}</p>;
      $[0] = violator;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  return violator;
};
const Title = props => {
  const $ = _c(5);
  const {
    title
  } = useBaseTile();
  if (!title) {
    return null;
  }
  let t0;
  if ($[0] !== props.className) {
    t0 = cn("mb-2 text-body-md font-medium text-bodi-white", props.className);
    $[0] = props.className;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0 || $[3] !== title) {
    t1 = <p className={t0}>{title}</p>;
    $[2] = t0;
    $[3] = title;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const Description = t0 => {
  const $ = _c(5);
  const {
    className
  } = t0;
  const {
    description
  } = useBaseTile();
  if (!description) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn("text-body-sm font-normal text-bodi-gray-disabled", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== description || $[3] !== t1) {
    t2 = <div className={t1}>{description}</div>;
    $[2] = description;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const Footer = () => {
  const $ = _c(2);
  const {
    footerText
  } = useBaseTile();
  if (!footerText) {
    return null;
  }
  let t0;
  if ($[0] !== footerText) {
    t0 = <p className="text-body-sm font-normal text-bodi-gray-disabled">{footerText}</p>;
    $[0] = footerText;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
const DescriptionLong = t0 => {
  const $ = _c(5);
  const {
    className
  } = t0;
  const {
    descriptionLong
  } = useBaseTile();
  if (!descriptionLong) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn("mt-8 text-body-md font-normal text-bodi-gray-butters", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== descriptionLong || $[3] !== t1) {
    t2 = <p className={t1}>{descriptionLong}</p>;
    $[2] = descriptionLong;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const ProgressBar = () => {
  const $ = _c(2);
  const {
    progress
  } = useBaseTile();
  if (!progress) {
    return null;
  }
  const t0 = `${progress}%`;
  let t1;
  if ($[0] !== t0) {
    t1 = <div className="absolute right-2 bottom-2 left-2 lg:right-4 lg:bottom-4 lg:left-4"><div className="h-2 w-full rounded-full bg-bodi-white/60"><div className="h-2 rounded-full bg-bodi-progress-fill" style={{
          width: t0
        }} /></div></div>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const PlayIcon = t0 => {
  const $ = _c(7);
  const {
    size,
    className
  } = t0;
  const {
    isPlayable
  } = useBaseTile();
  if (!isPlayable) {
    return null;
  }
  const dimensions = size === "large" ? 100 : size === "medium" ? 70 : 40;
  let t1;
  if ($[0] !== className) {
    t1 = cn("absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== dimensions) {
    t2 = <NextImage src="/images/play-white.svg" alt="Play" width={dimensions} height={dimensions} />;
    $[2] = dimensions;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const LockIcon = t0 => {
  const $ = _c(8);
  const {
    size,
    className,
    alwaysShow
  } = t0;
  const {
    isLocked
  } = useBaseTile();
  if (!isLocked) {
    return null;
  }
  const dimensions = size === "large" ? 100 : size === "medium" ? 70 : 40;
  const visibilityClasses = alwaysShow ? "opacity-100" : "opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100";
  let t1;
  if ($[0] !== className || $[1] !== visibilityClasses) {
    t1 = cn("absolute inset-0 flex items-center justify-center", visibilityClasses, className);
    $[0] = className;
    $[1] = visibilityClasses;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== dimensions) {
    t2 = <NextImage src="/images/lock-white.svg" alt="Locked" width={dimensions} height={dimensions} />;
    $[3] = dimensions;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const DietaryGroups = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = rest;
  } else {
    children = $[1];
    className = $[2];
    rest = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex gap-1", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== rest || $[8] !== t1) {
    t2 = <div className={t1} {...rest}>{children}</div>;
    $[6] = children;
    $[7] = rest;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
const DietaryGroup = t0 => {
  const $ = _c(9);
  const {
    label,
    backgroundColor,
    foregroundColor,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex h-6 w-6 items-center justify-center rounded-full text-body-xxs font-extrabold uppercase", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== backgroundColor || $[3] !== foregroundColor) {
    t2 = {
      backgroundColor,
      color: foregroundColor
    };
    $[2] = backgroundColor;
    $[3] = foregroundColor;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== label || $[6] !== t1 || $[7] !== t2) {
    t3 = <div className={t1} style={t2}>{label}</div>;
    $[5] = label;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const Actions = () => {
  const $ = _c(15);
  const {
    isFavoritable,
    isFavorited,
    isLocked,
    shareUrl,
    showShare,
    onFavoriteClick,
    onShareClick,
    entityId,
    entityType,
    showFavorite,
    title
  } = useBaseTile();
  const canFavorite = isFavoritable && !isLocked;
  const canShare = showShare && !!shareUrl;
  if (!canFavorite && !canShare) {
    return null;
  }
  let t0;
  if ($[0] !== canFavorite || $[1] !== entityId || $[2] !== entityType || $[3] !== isFavorited || $[4] !== onFavoriteClick || $[5] !== showFavorite) {
    t0 = canFavorite && showFavorite && <FavoriteButton entityId={entityId} entityType={entityType} isFavorited={isFavorited} onFavoriteClick={onFavoriteClick} />;
    $[0] = canFavorite;
    $[1] = entityId;
    $[2] = entityType;
    $[3] = isFavorited;
    $[4] = onFavoriteClick;
    $[5] = showFavorite;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  let t1;
  if ($[7] !== canShare || $[8] !== onShareClick || $[9] !== shareUrl || $[10] !== title) {
    t1 = canShare && <ShareButton shareUrl={shareUrl} title={title} onShareClick={onShareClick} />;
    $[7] = canShare;
    $[8] = onShareClick;
    $[9] = shareUrl;
    $[10] = title;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== t0 || $[13] !== t1) {
    t2 = <div className="relative z-10 mt-2 flex gap-1">{t0}{t1}</div>;
    $[12] = t0;
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
};
export const BaseTile = {
  Provider,
  Link,
  Image,
  Content,
  Violator,
  Title,
  Wrapper,
  Thumbnail,
  Footer,
  Description,
  DescriptionLong,
  ProgressBar,
  PlayIcon,
  LockIcon,
  DietaryGroups,
  DietaryGroup,
  Actions
};